import type { Scope } from "@sentry/react";

import { isInAppBrowser } from "../utils";
import { getAnalyticsSampleRate, getDappUiSentryScope, initDappUiSentry } from "./client";
import { getDappUiAnonId, hashWalletAddress } from "./user-id";

export type DappUiFeature =
  | "app_launcher"
  | "bridge"
  | "connect_button"
  | "send"
  | "skills"
  | "wallet_connect"
  | "wallet_info"
  | "wallet_portfolio";

export type DappUiFlow = "connect" | "send" | "bridge" | "withdraw";

export type DappUiFailureReason =
  | "user-rejected"
  | "insufficient-gas"
  | "timeout"
  | "contract-reverted"
  | "network"
  | "unknown";

export interface TrackDappUiFunnelOptions {
  status?: "success" | "failure";
  reason?: DappUiFailureReason;
  tags?: Record<string, string>;
}

type DeviceType = "desktop" | "mobile" | "tablet";

function detectDeviceType(): DeviceType {
  const ua = window.navigator.userAgent;
  if (/ipad|tablet|(android(?!.*mobile))/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android/i.test(ua)) return "mobile";
  return "desktop";
}

function commonTags(): Record<string, string> {
  return {
    host: window.location.hostname,
    device_type: detectDeviceType(),
    is_cross_inapp: String(isInAppBrowser()),
  };
}

let userInitialized = false;

/** Lazy-inits the isolated client and stamps the anon user id on first use. */
function ensureAnalyticsScope(): Scope | null {
  if (typeof window === "undefined") return null;
  const scope = getDappUiSentryScope() ?? initDappUiSentry();
  if (!scope) return null;
  if (!userInitialized) {
    userInitialized = true;
    scope.setUser({ id: getDappUiAnonId() });
  }
  return scope;
}

function sampledOut(): boolean {
  return Math.random() >= getAnalyticsSampleRate();
}

/**
 * `wallet_*` while an address is connected, `anon_*` otherwise. Called
 * automatically by WalletInfo/ConnectButton from their address props; hosts
 * only need it for custom setups. Only a truncated hash of the address is sent.
 */
let userUpdateSeq = 0;

export function setDappUiAnalyticsUser(address?: string): void {
  const scope = ensureAnalyticsScope();
  if (!scope) return;
  const seq = ++userUpdateSeq;
  if (!address) {
    scope.setUser({ id: getDappUiAnonId() });
    return;
  }
  void hashWalletAddress(address).then((walletId) => {
    // A newer call (e.g. disconnect) may have landed while hashing — don't clobber it.
    if (walletId && seq === userUpdateSeq) scope.setUser({ id: walletId });
  });
}

/** Button/UI usage event: message `ui: <feature>_<action>` on the isolated client. */
export function trackDappUiEvent(
  feature: DappUiFeature,
  action: string,
  tags?: Record<string, string>,
): void {
  const scope = ensureAnalyticsScope();
  if (!scope || sampledOut()) return;
  scope.captureMessage(`ui: ${feature}_${action}`, "info", {
    captureContext: { tags: { ...commonTags(), feature, action, ...tags } },
  });
}

/** Funnel step event: message `funnel: <flow>_<step>` with funnel_status/failure_reason tags. */
export function trackDappUiFunnel(
  flow: DappUiFlow,
  step: string,
  options?: TrackDappUiFunnelOptions,
): void {
  const scope = ensureAnalyticsScope();
  if (!scope || sampledOut()) return;
  const status = options?.status ?? "success";
  scope.captureMessage(`funnel: ${flow}_${step}`, "info", {
    captureContext: {
      tags: {
        ...commonTags(),
        flow,
        funnel_step: `${flow}_${step}`,
        funnel_status: status,
        ...(status === "failure" ? { failure_reason: options?.reason ?? "unknown" } : {}),
        ...options?.tags,
      },
    },
  });
}

/** Maps arbitrary wallet/RPC errors onto the fixed failure_reason vocabulary. */
export function normalizeFailureReason(error: unknown): DappUiFailureReason {
  const err = error as { code?: unknown; message?: unknown } | null | undefined;
  const message = String(
    (err && typeof err === "object" && "message" in err ? err.message : error) ?? "",
  ).toLowerCase();

  if (err?.code === 4001 || /reject|denied|declin|cancel/i.test(message)) return "user-rejected";
  if (/insufficient (funds|gas|balance)|exceeds balance/i.test(message)) return "insufficient-gas";
  if (/timeout|timed out|deadline/i.test(message)) return "timeout";
  if (/revert|execution failed/i.test(message)) return "contract-reverted";
  if (/network|fetch|connection|disconnected|offline/i.test(message)) return "network";
  return "unknown";
}
